import type { HandshakeDto } from '@/core/data/handshake/handshake.interface';
import { gambits, localize } from '@/core/gambits';
import { useAuthStore } from '@/stores/auth.store';
import { useConfetti } from '@/views/composables/hooks/useConfetti';
import { celebrateHandshake, rememberHandshake } from '@/views/main/handshakes/utils/handshake-celebration';
import { codeShape, filterCode } from '@/views/main/handshakes/utils/handshake-code';
import { storeToRefs } from 'pinia';
import { inject, onBeforeUnmount, provide, reactive, watchEffect, type InjectionKey } from 'vue';
import { useToast } from 'vue-toastification';

export interface HandshakeCodeOptions {
  /** A clock's game is written onto the handshake, so both draw the same one. */
  fromClock: () => boolean;
  suggestionSeed: () => string | undefined;
  suggestionRound: () => number | undefined;
  /**
   * A passerby just cut stays on this sheet as the code. Otherwise the caller
   * is told to leave, which is what the clock's own code sheet does.
   */
  retainPasserby: () => boolean;
  /** `existing` is the live handshake the code led back to, so its game opens rather than a new one. */
  onHandshake: (handshake: HandshakeDto, existing?: boolean) => void;
  /** The sheet has nothing left to show: a claim, a confirmed code, or a passerby it does not keep. */
  onLeave: () => void;
}

export interface HandshakeCodeController {
  code: string | null;
  expiresIn: number;
  inputCode: string;
  resolving: boolean;
  inputError: boolean;
  waitingForUsername: string | null;
  passerbyCode: string | null;
  passerbyHandshake: HandshakeDto | null;
  creating: boolean;
  isPasserbyEntry: boolean;
  codeReady: boolean;
  showSubmit: boolean;
  start: () => void;
  reset: () => void;
  generate: () => Promise<void>;
  refresh: () => void;
  createPasserby: () => Promise<boolean>;
  applyPasserby: (handshake: HandshakeDto) => void;
  onInput: () => Promise<void>;
}

const handshakeCodeKey: InjectionKey<HandshakeCodeController> = Symbol('handshake-code');

/** The code a sheet is sharing or typing. The sheet owns it, so hiding the face does not forget it. */
export function provideHandshakeCode(options: HandshakeCodeOptions): HandshakeCodeController {
  const toast = useToast();
  const { user } = storeToRefs(useAuthStore());
  const { fire: fireConfetti } = useConfetti();

  const api = reactive<HandshakeCodeController>({
    code: null,
    expiresIn: 0,
    inputCode: '',
    resolving: false,
    inputError: false,
    waitingForUsername: null,
    passerbyCode: null,
    passerbyHandshake: null,
    creating: false,
    isPasserbyEntry: false,
    codeReady: false,
    showSubmit: true,
    start: () => undefined,
    reset: () => undefined,
    generate: async () => undefined,
    refresh: () => undefined,
    createPasserby: async () => false,
    applyPasserby: () => undefined,
    onInput: async () => undefined,
  });

  let claiming = false;
  let expiryTimer: ReturnType<typeof setInterval> | null = null;

  const stopExpiryTimer = () => {
    if (!expiryTimer) return;

    clearInterval(expiryTimer);
    expiryTimer = null;
  };

  const startExpiryTimer = () => {
    stopExpiryTimer();
    expiryTimer = setInterval(() => {
      api.expiresIn = Math.max(0, api.expiresIn - 1);
      if (api.expiresIn <= 0) stopExpiryTimer();
    }, 1000);
  };

  /** The clock's game, sent only when this sheet was opened from one. */
  const clockGame = () => {
    const seed = options.suggestionSeed();
    if (!options.fromClock() || !seed) return {};

    return { suggestionSeed: seed, suggestionRound: options.suggestionRound() ?? 0 };
  };

  const reset = () => {
    api.code = null;
    api.expiresIn = 0;
    api.inputCode = '';
    api.inputError = false;
    api.resolving = false;
    api.waitingForUsername = null;
    api.passerbyCode = null;
    api.passerbyHandshake = null;
    api.creating = false;
    claiming = false;
    stopExpiryTimer();
  };

  const applyPasserby = (handshake: HandshakeDto) => {
    if (!handshake.passerbyClaimCode) return;

    api.passerbyCode = handshake.passerbyClaimCode;
    api.passerbyHandshake = handshake;
    api.code = null;
    api.expiresIn = 0;
    stopExpiryTimer();
  };

  const generate = async () => {
    api.code = null;
    api.expiresIn = 0;

    try {
      const result = await gambits.userService.generateHandshakeCode();
      if (result?.data) {
        api.code = result.data.code;
        const ms = new Date(result.data.expiresAt).getTime() - Date.now();
        api.expiresIn = Math.max(0, Math.floor(ms / 1000));
        startExpiryTimer();
        return;
      }
    } catch {
      // fall through
    }

    toast.error(localize('handshake.code.error'));
  };

  /**
   * A code to share, unless one is already on the sheet: a passerby just cut,
   * a person still typing, or a member code that has not run out.
   */
  const start = () => {
    if (api.passerbyCode) return;
    if (api.waitingForUsername && api.code) return;
    if (api.code && api.expiresIn > 0) return;

    reset();
    void generate();
  };

  const createPasserby = async (): Promise<boolean> => {
    if (api.creating) return false;

    api.creating = true;
    const result = await gambits.userService.createPasserbyHandshake(clockGame());
    api.creating = false;

    if (result.error || !result.data?.passerbyClaimCode) {
      toast.error(result.error?.message || localize('handshake.passerby.error'));
      return false;
    }

    if (options.retainPasserby()) applyPasserby(result.data);
    options.onHandshake(result.data);
    if (!options.retainPasserby()) options.onLeave();

    return true;
  };

  const submitPasserbyCode = async (value: string): Promise<boolean> => {
    claiming = true;
    const claimed = await gambits.userService.claimPasserbyHandshake(value);
    claiming = false;

    if (claimed.error || !claimed.data) {
      toast.error(claimed.error?.message || localize('handshake.code.invalidCode'));
      return false;
    }

    celebrateHandshake(claimed.data.id, fireConfetti);
    options.onHandshake(claimed.data);
    options.onLeave();

    return true;
  };

  const onInput = async () => {
    if (api.resolving || claiming) return;

    api.inputError = false;

    const shape = codeShape(api.inputCode.toLowerCase());
    if (!shape) {
      api.inputCode = '';
      return;
    }

    api.inputCode = filterCode(api.inputCode.toLowerCase(), shape);
    if (api.inputCode.length !== shape.length) return;

    if (!shape.startsWithLetter) {
      api.resolving = true;
      const claimed = await submitPasserbyCode(api.inputCode);
      api.resolving = false;

      if (!claimed) {
        api.inputError = true;
        api.inputCode = '';
      }

      return;
    }

    api.resolving = true;
    try {
      const resolved = await gambits.userService.resolveHandshakeCode(api.inputCode);
      if (!resolved?.data) throw new Error('invalid');

      const { userId, username, token } = resolved.data;

      if (userId === user.value?.id) {
        toast.error(localize('handshake.code.self'));
        api.inputError = true;
        api.inputCode = '';
        api.resolving = false;
        return;
      }

      const offer = await gambits.userService.offerHandshake(userId, null, {
        token,
        ...clockGame(),
      });
      if (offer.error || !offer.data) {
        toast.error(offer.error?.message || localize('handshake.code.invalidCode'));
        api.inputError = true;
        api.inputCode = '';
        api.resolving = false;
        return;
      }

      // First typer: the sheet stays up, code and all, until the other person types it.
      if (!offer.data.isConfirmed) {
        api.waitingForUsername = username;
        api.inputCode = '';
        api.resolving = false;
        options.onHandshake(offer.data);
        return;
      }

      const isNew = offer.status === 201;
      if (isNew) {
        celebrateHandshake(offer.data.id, fireConfetti);
        options.onHandshake(offer.data);
      } else {
        rememberHandshake(offer.data.id);
        options.onHandshake(offer.data, true);
      }

      options.onLeave();
    } catch {
      toast.error(localize('handshake.code.invalidCode'));
      api.inputError = true;
      api.inputCode = '';
      api.resolving = false;
    }
  };

  api.start = start;
  api.reset = reset;
  api.generate = generate;
  api.refresh = () => void generate();
  api.createPasserby = createPasserby;
  api.applyPasserby = applyPasserby;
  api.onInput = onInput;

  watchEffect(() => {
    const shape = codeShape(api.inputCode.toLowerCase());
    api.isPasserbyEntry = !!shape && !shape.startsWithLetter;
    api.codeReady = !!shape && api.inputCode.length === shape.length;
    api.showSubmit = !api.passerbyCode && !api.waitingForUsername;
  });

  onBeforeUnmount(stopExpiryTimer);
  provide(handshakeCodeKey, api);

  return api;
}

/** The face of the code the surrounding sheet provided. */
export function useProvidedHandshakeCode(): HandshakeCodeController {
  const api = inject(handshakeCodeKey);
  if (!api) throw new Error('HandshakeCodePanel must stand inside a sheet that provides a handshake code');

  return api;
}
